import { ColumnDef, sortingFns } from '@tanstack/react-table';
import React, { useMemo } from 'react';
import { getRiskConsequencesName, getRiskEventsName, getRiskSourcesName, getScenarioStatusName } from 'src/api/types';
import { GTColumnSelectAnchor, GTTdm } from 'src/components/GenericTable';
import { formatDate, numeralFormat } from 'src/utils/misc';
import { filter } from 'lodash';
import { Link } from 'react-router-dom';
import { GTInternalIds } from 'src/components/GenericTable/types';
import { queryCache } from 'react-query';
import { IncompleteStateMarkerNew, TextOverflowTooltip } from 'src/common';
import { mpEvent } from 'src/utils/mixpanel/useMixPanel';
import { MPEvents } from 'src/utils/mixpanel/types';
import useVersion from 'src/hooks/useVersion';
import { encodeQueryParams } from 'src/api/client';
import { TableIds } from 'src/api/types/misc';
import useLocalStorage from 'src/hooks/useLocalStorage';
import { TLLink } from '../../../DashboardPage/comps';
import { IncidentQuery, useUnlinkIncidentFromScenariosMutation } from 'src/graphql/_generated';
import { useCache } from 'src/graphql';

type IncidentScenario = NonNullable<IncidentQuery['incident']>['scenarios'][number];

type UseColumns = (a: { data?: IncidentScenario[]; incidentId: string }) => {
  columns: ColumnDef<IncidentScenario>[];
  GTColumnSelectAnchorExported: JSX.Element;
};

const defaultColumns = ['status_scen', 'name', 'source', 'event', 'consequence', 'expected_loss', 'updated_at'];
export const useScenarioColumns: UseColumns = ({ data = [], incidentId }) => {
  const [selectedColumns, setSelectedColumns] = useLocalStorage(
    `${TableIds.incidentSecnarios}-columns`,
    defaultColumns,
  );
  const { version, assessmentId } = useVersion();
  const { refetchQuery } = useCache();

  const [unlink] = useUnlinkIncidentFromScenariosMutation({
    onCompleted: () => {
      queryCache.invalidateQueries();
      refetchQuery({ include: ['incidents', 'incident'] });
    },
  });

  const columns = useMemo(() => {
    const internalIds = [GTInternalIds.tdm];

    const list: ColumnDef<IncidentScenario>[] = [
      {
        id: 'assessment_name',
        header: 'Assessment',
        accessorFn: (row) => row.assessment?.name,
        cell: (info) => info.getValue(),
        sortUndefined: 'last',
        sortingFn: sortingFns.text,
      },
      {
        id: 'status_scen',
        header: 'Status',
        accessorFn: (row) => getScenarioStatusName(row.status),
        cell: (info) => info.getValue(),
      },
      {
        id: 'name',
        header: 'Name',
        accessorFn: (row) => row.name,
        cell: ({ row }) => (
          <TextOverflowTooltip>
            <Link
              className="table-link"
              to={`/risk-assessments/${row.original.assessment?.id}/scenario/${row.original.id}${
                version ? encodeQueryParams({ version, assessmentId }) : ''
              }`}
              onClick={(e) => {
                e.stopPropagation();
                mpEvent(MPEvents.Link, {
                  source: {
                    value: ['Incident'],
                    params: {
                      id: incidentId,
                    },
                  },
                  destination: { value: ['Scenario'], params: { id: row.original.id } },
                  tags: ['SCENARIO', 'INCIDENT'],
                });
              }}
            >
              {row.original.name}
            </Link>
          </TextOverflowTooltip>
        ),
        sortUndefined: 'last',
        sortingFn: sortingFns.text,
      },
      {
        id: 'source',
        header: 'Source',
        accessorFn: (row) => getRiskSourcesName(row.source),
        cell: (info) => info.getValue(),
      },
      {
        id: 'event',
        header: 'Event',
        accessorFn: (row) => getRiskEventsName(row.event),
        cell: (info) => info.getValue(),
      },
      {
        id: 'consequence',
        header: 'Consequence',
        accessorFn: (row) => getRiskConsequencesName(row.consequence),
        cell: (info) => info.getValue(),
      },
      {
        id: 'risk_owner',
        header: 'Risk Owner',
        accessorFn: (row) => row.ownerName || null,
        cell: (info) => info.getValue(),
        sortingFn: sortingFns.text,
      },
      {
        id: 'expected_loss',
        header: 'Expected Loss',
        accessorFn: (row) => row.liveRisk?.expectedLoss || null,
        cell: (info) => numeralFormat(info.getValue() as number),
      },
      {
        id: '_control_count',
        header: 'Linked Controls',
        accessorFn: (row) => row.riskManagementPlans?.length || 0,
        cell: (info) => info.getValue(),
      },
      {
        id: 'risk_identified_date',
        header: 'Identified Date',
        accessorFn: (row) => row.riskIdentifiedDate || '',
        cell: (info) => <TLLink date={info.getValue() as string}>{formatDate(info.getValue() as string)}</TLLink>,
      },
      {
        id: 'updated_at',
        header: 'Last Updated',
        accessorFn: (row) => row.meta?.updatedAt || null,
        cell: ({ row }) => {
          const date = row.original.meta?.updatedAt;
          if (!date) return null;
          return <TLLink date={date}>{formatDate(date, { formatType: 'datetime' })}</TLLink>;
        },
      },
      {
        id: 'created_at',
        header: 'Created Date',
        accessorFn: (row) => row.meta?.createdAt || null,
        cell: ({ row }) => {
          const date = row.original.meta?.createdAt;
          if (!date) return null;
          return <TLLink date={date}>{formatDate(date, { formatType: 'datetime' })}</TLLink>;
        },
      },
      {
        id: GTInternalIds.tdm,
        header: () => null,
        cell: ({ row }) => {
          const onUnlinkScenarioClick = !version
            ? () =>
                unlink({
                  variables: {
                    incidentId,
                    scenarioIds: [row.original.id],
                  },
                })
            : undefined;
          return (
            <div css="display: flex;grid-gap: 8px">
              <GTTdm
                extraComponentLeft={<IncompleteStateMarkerNew scenario={row.original} />}
                onUnlinkScenarioClick={onUnlinkScenarioClick}
              />
            </div>
          );
        },
        size: 1,
      },
    ];

    return filter(list, (el) => [...internalIds, ...selectedColumns].includes(el.id as string));
  }, [data, selectedColumns, version, assessmentId]);

  const GTColumnSelectAnchorExported = (
    <GTColumnSelectAnchor
      tableId={TableIds.incidentSecnarios}
      options={[
        { key: 'assessment_name', title: 'Assessment' },
        { key: 'status_scen', title: 'Status' },
        { key: 'name', title: 'Name' },
        { key: 'source', title: 'Source' },
        { key: 'event', title: 'Event' },
        { key: 'consequence', title: 'Consequence' },
        {
          key: 'risk_owner',
          title: 'Risk Owner',
        },
        {
          key: 'expected_loss',
          title: 'Expected Loss',
        },
        {
          key: '_control_count',
          title: 'Linked Controls',
        },
        {
          key: 'risk_identified_date',
          title: 'Identified Date',
        },
        {
          key: 'updated_at',
          title: 'Last Updated',
        },
        {
          key: 'created_at',
          title: 'Created Date',
        },
      ]}
      defaultOptions={defaultColumns}
      value={selectedColumns}
      onChange={setSelectedColumns}
    />
  );

  return { columns, GTColumnSelectAnchorExported };
};
